import { type IMaterial, type IKHRMaterialsScatter } from "babylonjs-gltf2interface";
import { type IGLTFExporterExtensionV2 } from "../glTFExporterExtension";
import { GLTFExporter } from "../glTFExporter";
import { type Material } from "core/Materials/material";
import { OpenPBRMaterial } from "core/Materials/PBR/openpbrMaterial.pure";
import { type BaseTexture } from "core/Materials/Textures/baseTexture";
import { Color3, Color4 } from "core/Maths/math.color.pure";
import { type Nullable } from "core/types";
import {
    type ITextureProcessOperand,
    type ITextureProcessorOutputOptions,
    ChannelMask,
    CreateFactorOperand,
    CreateTextureOperand,
    CreateTextureWithFactorOperand,
    ExtractChannelAsync,
    InvertTextureAsync,
    LerpTexturesAsync,
    MultiplyTexturesAsync,
    SingleScatterToMultiScatterAlbedoAsync,
    TextureChannel,
    TextureColorSpace,
    TransmissionFractionAsync,
    TransmissionScatterToSingleScatterAlbedoAsync,
} from "core/Materials/Textures/textureProcessor";
import { Constants } from "core/Engines/constants";
import { type Scene } from "core/scene";
import { MergeTexturesAsync, CreateRGBAConfiguration, CreateTextureInput, CreateConstantInput } from "core/Materials/Textures/textureMerger";
import { Texture } from "core/Materials/Textures/texture.pure";
import { type InternalTexture } from "core/Materials/Textures/internalTexture";
import { CopyTextureSampling, GetOpenPBRVolumeAttenuation, MinTransmissionColor } from "../glTFUtilities";

const NAME = "KHR_materials_scatter";

/**
 * Render-target options for intermediate passes: floating point when the engine can render to it, so
 * only the final exported textures are quantized to 8 bits.
 * @param scene the scene whose engine renders the passes
 * @returns output options for an intermediate texture processor pass
 */
function IntermediateOutputOptions(scene: Scene): ITextureProcessorOutputOptions {
    const caps = scene.getEngine().getCaps();
    return {
        textureType: caps.textureFloatRender ? Constants.TEXTURETYPE_FLOAT : caps.textureHalfFloatRender ? Constants.TEXTURETYPE_HALF_FLOAT : Constants.TEXTURETYPE_UNSIGNED_BYTE,
        samplingMode: Constants.TEXTURE_NEAREST_SAMPLINGMODE,
    };
}

/**
 * Creates a texture that references an existing internal texture with the sampling parameters of another texture,
 * so several glTF texture references can share one image while keeping their own UV set and transform.
 * @param name the name of the new texture
 * @param internalTexture the internal texture to reference
 * @param samplingSource the texture to copy the UV set, wrap modes and transform from
 * @returns the new texture, which must be disposed by the caller
 */
function CreateTextureReference(name: string, internalTexture: InternalTexture, samplingSource: BaseTexture): Texture {
    const reference = new Texture(null, samplingSource.getScene());
    reference.name = name;
    reference._texture = internalTexture;
    internalTexture.incrementReferences();
    CopyTextureSampling(samplingSource, reference);
    return reference;
}

// Scatter result cached per material between postExportMaterialAdditionalTexturesAsync (where it is
// baked) and postExportMaterialAsync (where it is referenced). `ownedTextures` holds the textures this
// extension created (as opposed to raw material textures it references) and are disposed in dispose().
type ScatterResult = {
    strengthFactor: number;
    strengthTexture: Nullable<BaseTexture>;
    colorFactor: [number, number, number];
    colorTexture: Nullable<BaseTexture>;
    anisotropy: number;
    ownedTextures: BaseTexture[];
};

/**
 * TODO: In-progress specification
 * [Specification](https://github.com/KhronosGroup/glTF/blob/7ea427ed55d44427e83c0a6d1c87068b1a4151c5/extensions/2.0/Khronos/KHR_materials_scatter/README.md)
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export class KHR_materials_scatter implements IGLTFExporterExtensionV2 {
    /** Name of this extension */
    public readonly name = NAME;

    /** Defines whether this extension is enabled */
    public enabled = true;

    /** Defines whether this extension is required */
    public required = false;

    private _exporter: GLTFExporter;

    private _wasUsed = false;

    private _scatterResults = new Map<Material, ScatterResult>();

    constructor(exporter: GLTFExporter) {
        this._exporter = exporter;
    }

    public dispose() {
        for (const result of this._scatterResults.values()) {
            for (const texture of result.ownedTextures) {
                texture.dispose();
            }
        }
        this._scatterResults.clear();
    }

    /** @internal */
    public get wasUsed() {
        return this._wasUsed;
    }

    /**
     * After exporting a material, bake the combined multi-scatter color texture (if any).
     * @param context GLTF context of the material
     * @param node exported GLTF node
     * @param babylonMaterial corresponding babylon material
     * @returns array of additional textures to export
     */
    public async postExportMaterialAdditionalTexturesAsync?(context: string, node: IMaterial, babylonMaterial: Material): Promise<BaseTexture[]> {
        const additionalTextures: BaseTexture[] = [];

        if (babylonMaterial instanceof OpenPBRMaterial && this._isExtensionEnabled(babylonMaterial)) {
            const result = await this._bakeScatterAsync(babylonMaterial);
            this._scatterResults.set(babylonMaterial, result);
            if (result.strengthTexture) {
                additionalTextures.push(result.strengthTexture);
            }
            if (result.colorTexture) {
                additionalTextures.push(result.colorTexture);
            }
        }

        return additionalTextures;
    }

    private _isExtensionEnabled(mat: OpenPBRMaterial): boolean {
        // This extension must not be used on a material that also uses KHR_materials_unlit
        if (mat.unlit) {
            return false;
        }
        // Scattering is enabled if either subsurface or transmission scattering is enabled.
        // Transmission scattering is only enabled if the material is not thin-walled, and has a non-zero transmission depth.
        const transmissionVolume = mat.transmissionWeight > 0 && !mat.geometryThinWalled && mat.transmissionDepth > 0;
        const transmissionScatter = transmissionVolume && !mat.transmissionScatter.equals(Color3.Black());
        if (transmissionScatter || mat.subsurfaceWeight > 0) {
            return true;
        }
        return false;
    }

    /**
     * Bake the KHR_materials_scatter data for an OpenPBR material, dispatching on thin-walled vs volumetric.
     * @param mat the OpenPBR material to bake scatter data for
     * @returns the scatter strength, multi-scatter color, and anisotropy (constants and/or baked textures)
     */
    private async _bakeScatterAsync(mat: OpenPBRMaterial): Promise<ScatterResult> {
        return mat.geometryThinWalled ? await this._bakeThinWalledScatterAsync(mat) : await this._bakeVolumetricScatterAsync(mat);
    }

    /**
     * Per-pixel transmission fraction operand `T / (T + S(1 - T))` (= `T / (1 - (1 - T)(1 - S))`) from the
     * (possibly textured) transmission weight `T` and subsurface weight `S`. Folds to a constant when
     * neither weight has a texture. The subsurface fraction is `1 - transmissionFraction`. Weights are read
     * from the glTF-convention channels (transmission R; subsurface A when packed in alpha, otherwise R).
     * The fraction is computed in one fused pass into a floating-point target (when supported), so small
     * weights don't cancel out in 8-bit intermediates; callers quantize only their final output.
     * @param mat the OpenPBR material whose weights drive the fraction
     * @returns an operand carrying the transmission fraction (constant or baked texture)
     */
    private async _computeTransmissionFractionAsync(mat: OpenPBRMaterial): Promise<ITextureProcessOperand> {
        const scene = mat.getScene();
        const transmissionWeight = mat.transmissionWeight;
        const subsurfaceWeight = mat.subsurfaceWeight;
        const subsurfaceChannel = mat._useSubsurfaceWeightFromTextureAlpha ? TextureChannel.A : TextureChannel.R;
        const transmissionWeightOp = CreateTextureWithFactorOperand(
            mat.transmissionWeightTexture,
            new Color4(transmissionWeight, transmissionWeight, transmissionWeight, 1.0),
            TextureChannel.R
        );
        const subsurfaceWeightOp = CreateTextureWithFactorOperand(
            mat.subsurfaceWeightTexture,
            new Color4(subsurfaceWeight, subsurfaceWeight, subsurfaceWeight, 1.0),
            subsurfaceChannel
        );

        return await TransmissionFractionAsync(
            `scatter transmission fraction (${mat.name})`,
            transmissionWeightOp,
            subsurfaceWeightOp,
            scene,
            undefined,
            undefined,
            IntermediateOutputOptions(scene)
        );
    }

    /**
     * Bake the constant slab fractions used only for the scatter anisotropy blend (glTF has no
     * anisotropy texture, so anisotropy always uses the constant weights).
     * @param mat the OpenPBR material whose constant weights drive the fractions
     * @returns the constant transmission and subsurface fractions of the dielectric
     */
    private _constantFractions(mat: OpenPBRMaterial): { transmissionFraction: number; subsurfaceFraction: number } {
        const subsurfaceFractionOfDielectric = (1.0 - mat.transmissionWeight) * mat.subsurfaceWeight;
        const denominator = subsurfaceFractionOfDielectric + mat.transmissionWeight;
        const reciprocal = 1.0 / Math.max(denominator, 1e-6);
        return { transmissionFraction: mat.transmissionWeight * reciprocal, subsurfaceFraction: subsurfaceFractionOfDielectric * reciprocal };
    }

    /**
     * Volumetric scatter: glTF stores a single multi-scatter color blended from the transmission and
     * subsurface slabs, with `scatterStrength = 1`.
     *
     * OpenPBR represents the two slabs as:
     *  - The transmission slab stores `transmission_scatter = extinction * singleScatterAlbedo` (OpenPBR 1.1),
     *    where the renderer's extinction is `-log(transmissionColor)` with the absorption shifted to be
     *    non-negative. We recover the single-scatter albedo by dividing the scattering coefficient by the
     *    extinction emitted by KHR_materials_volume (which the importer multiplies back), then convert it
     *    to a multi-scatter albedo (the quantity KHR_materials_scatter stores). glTF attenuation can't be
     *    textured, so the emitted extinction is derived from the untextured factors; texels whose scattering
     *    coefficient exceeds it are approximated by clamping the albedo to one. Without a transmission depth
     *    no extinction is emitted, so the per-texel renderer extinction is used instead.
     *  - The subsurface slab stores `subsurface_color`, which is already a multi-scatter albedo.
     *
     * The two are blended by `lerp(subsurface, transmission, transmissionFraction)` (the two fractions
     * sum to one). The whole computation is exact per-pixel when any slab or weight carries a texture,
     * and folds to a constant factor when none do.
     *
     * TODO: OpenPBR 1.2 will define `transmission_scatter` as the single-scatter albedo directly,
     * which removes the extinction division below.
     * @param mat the OpenPBR material to bake volumetric scatter data for
     * @returns scatter strength (always 1), the blended multi-scatter color, and blended anisotropy
     */
    private async _bakeVolumetricScatterAsync(mat: OpenPBRMaterial): Promise<ScatterResult> {
        const scene = mat.getScene();
        const transmissionWeight = mat.transmissionWeight;
        const subsurfaceWeight = mat.subsurfaceWeight;

        // Transmission slab → multi-scatter albedo.
        let transmissionMultiOp: ITextureProcessOperand;
        if (transmissionWeight > 0) {
            const scatter = mat.transmissionScatter;
            const transmissionScatterOp = CreateTextureWithFactorOperand(mat.transmissionScatterTexture, new Color4(scatter.r, scatter.g, scatter.b, 1.0));
            const intermediateOptions = IntermediateOutputOptions(scene);
            let singleScatterOp: ITextureProcessOperand;
            if (mat.transmissionDepth > 0) {
                // Divide by the extinction that KHR_materials_volume actually emits, so the importer's
                // `extinction * albedo` reproduces the scattering coefficient.
                const { attenuationColor, attenuationDistance } = GetOpenPBRVolumeAttenuation(mat, true, subsurfaceWeight > 0);
                const scale = (scatterChannel: number, attenuationChannel: number) => {
                    const extinction = -Math.log(Math.max(attenuationChannel, MinTransmissionColor)) / attenuationDistance;
                    return extinction > 0 ? scatterChannel / (mat.transmissionDepth * extinction) : 0;
                };
                singleScatterOp = CreateTextureWithFactorOperand(
                    mat.transmissionScatterTexture,
                    new Color4(scale(scatter.r, attenuationColor[0]), scale(scatter.g, attenuationColor[1]), scale(scatter.b, attenuationColor[2]), 1.0)
                );
            } else {
                const color = mat.transmissionColor;
                // Uses the renderer's extinction, which shifts negative absorption (per texel when textured).
                singleScatterOp = await TransmissionScatterToSingleScatterAlbedoAsync(
                    `scatter single-scatter (${mat.name})`,
                    CreateFactorOperand(new Color4(color.r, color.g, color.b, 1.0)),
                    transmissionScatterOp,
                    scene,
                    undefined,
                    undefined,
                    intermediateOptions
                );
            }
            transmissionMultiOp = await SingleScatterToMultiScatterAlbedoAsync(
                `scatter multi-scatter (${mat.name})`,
                singleScatterOp,
                scene,
                mat.transmissionScatterAnisotropy,
                intermediateOptions
            );
        } else {
            transmissionMultiOp = CreateFactorOperand(new Color4(0, 0, 0, 1.0));
        }

        // Subsurface slab → multi-scatter albedo (subsurface_color already is one).
        let subsurfaceMultiOp: ITextureProcessOperand;
        if (subsurfaceWeight > 0) {
            const color = mat.subsurfaceColor;
            const colorSpace = mat.subsurfaceColorTexture?.gammaSpace ? TextureColorSpace.SRGB : TextureColorSpace.Linear;
            subsurfaceMultiOp = CreateTextureWithFactorOperand(mat.subsurfaceColorTexture, new Color4(color.r, color.g, color.b, 1.0), TextureChannel.RGBA, colorSpace);
        } else {
            subsurfaceMultiOp = CreateFactorOperand(new Color4(0, 0, 0, 1.0));
        }

        // multiscatterColor = lerp(subsurface, transmission, transmissionFraction), kept linear in a floating-point
        // target (when supported) so that tiny albedos, such as those produced near anisotropy 1, survive until
        // the final bake.
        const transmissionFractionOp = await this._computeTransmissionFractionAsync(mat);
        const combined = await LerpTexturesAsync(
            `scatter multiscatter color (${mat.name})`,
            subsurfaceMultiOp,
            transmissionMultiOp,
            transmissionFractionOp,
            scene,
            undefined,
            ChannelMask.RGB,
            IntermediateOutputOptions(scene)
        );
        let colorFactor: [number, number, number] = [combined.factor?.r ?? 0, combined.factor?.g ?? 0, combined.factor?.b ?? 0];
        let colorTexture: Nullable<BaseTexture> = null;
        if (combined.texture) {
            // The exported texture is 8-bit sRGB, which rounds albedos below about 1e-4 to black. Move each
            // channel's maximum into multiscatterColorFactor and store the texels normalized to it, so the
            // magnitude survives export. Force opaque alpha and output sRGB, as the loader reads it as sRGB.
            const max = await this._readChannelMaximaAsync(combined.texture);
            colorFactor = [max[0], max[1], max[2]];
            const normalize = new Color4(max[0] > 0 ? 1 / max[0] : 1, max[1] > 0 ? 1 / max[1] : 1, max[2] > 0 ? 1 / max[2] : 1, 1.0);
            colorTexture = (
                await MultiplyTexturesAsync(
                    `scatter multiscatter color (${mat.name})`,
                    { texture: combined.texture, factor: normalize, dispose: combined.dispose },
                    CreateFactorOperand(new Color4(1, 1, 1, 1)),
                    scene,
                    TextureColorSpace.SRGB,
                    ChannelMask.RGB
                )
            ).texture;
        }

        const fractions = this._constantFractions(mat);
        const anisotropy = mat.transmissionScatterAnisotropy * fractions.transmissionFraction + mat.subsurfaceScatterAnisotropy * fractions.subsurfaceFraction;
        const ownedTextures: BaseTexture[] = [];
        if (colorTexture) {
            ownedTextures.push(colorTexture);
        }
        return {
            strengthFactor: 1.0,
            strengthTexture: null,
            colorFactor,
            colorTexture,
            anisotropy,
            ownedTextures,
        };
    }

    /**
     * Read back a texture and return the maximum of each RGB channel.
     * @param texture the texture to read (unsigned-byte, or floating-point read back as 32-bit floats)
     * @returns the per-channel maxima, normalized to [0, 1] for unsigned-byte textures
     */
    private async _readChannelMaximaAsync(texture: BaseTexture): Promise<[number, number, number]> {
        const pixels = await texture.readPixels();
        const max: [number, number, number] = [0, 0, 0];
        if (!pixels) {
            return [1, 1, 1];
        }
        const data = pixels instanceof Uint8Array ? pixels : new Float32Array(pixels.buffer, pixels.byteOffset, pixels.byteLength / 4);
        const scale = pixels instanceof Uint8Array ? 1 / 255 : 1;
        for (let i = 0; i < data.length; i += 4) {
            for (let c = 0; c < 3; c++) {
                const value = data[i + c] * scale;
                if (value > max[c]) {
                    max[c] = value;
                }
            }
        }
        return max;
    }

    /**
     * Thin-walled scatter: there is no volumetric slab, so the multi-scatter color is simply
     * `subsurface_color` and the scatter strength is the subsurface fraction of the dielectric
     * `S(1-T) / (1 - (1-T)(1-S))` (= `1 - transmissionFraction`). This is the exact inverse of the
     * loader's thin-walled `ThinWalledScatterWeightsAsync`, so it round-trips.
     * @param mat the OpenPBR material to bake thin-walled scatter data for
     * @returns the subsurface fraction as scatter strength, subsurface_color as multi-scatter color, and subsurface anisotropy
     */
    private async _bakeThinWalledScatterAsync(mat: OpenPBRMaterial): Promise<ScatterResult> {
        const scene = mat.getScene();

        // scatterStrength = subsurfaceFraction = 1 - transmissionFraction.
        const transmissionFractionOp = await this._computeTransmissionFractionAsync(mat);
        const subsurfaceFractionOp = await InvertTextureAsync(`scatter strength (${mat.name})`, transmissionFractionOp, scene, ChannelMask.RGB);

        const ownedTextures: BaseTexture[] = [];
        const strengthFactor = subsurfaceFractionOp.texture ? 1.0 : (subsurfaceFractionOp.factor?.r ?? 0);
        let strengthTexture: Nullable<BaseTexture> = null;
        // multiscatterColor is subsurface_color directly (factor and/or the raw material texture, not owned).
        let colorTexture: Nullable<BaseTexture> = mat.subsurfaceColorTexture;
        if (subsurfaceFractionOp.texture) {
            // KHR_materials_scatter reads scatterStrengthTexture from the alpha channel and multiscatterColorTexture
            // from RGB, so pack both into one image. They are referenced separately, so each keeps its own sampling.
            const fractionTexture = subsurfaceFractionOp.texture;
            const colorSource: Nullable<BaseTexture> = colorTexture
                ? ((await this._encodeColorTextureAsSrgbAsync(`scatter color (${mat.name})`, colorTexture, IntermediateOutputOptions(scene))) ?? colorTexture)
                : null;
            const packed = await MergeTexturesAsync(
                `scatter color and strength (${mat.name})`,
                CreateRGBAConfiguration(
                    colorSource ? CreateTextureInput(colorSource, 0) : CreateConstantInput(1.0),
                    colorSource ? CreateTextureInput(colorSource, 1) : CreateConstantInput(1.0),
                    colorSource ? CreateTextureInput(colorSource, 2) : CreateConstantInput(1.0),
                    CreateTextureInput(fractionTexture, 0)
                ),
                scene
            );
            if (colorSource && colorSource !== colorTexture) {
                colorSource.dispose();
            }
            ownedTextures.push(packed);

            const packedInternal = packed.getInternalTexture();
            if (colorTexture && packedInternal) {
                // Keep the source color filtering rather than the merger's nearest sampling.
                scene.getEngine().updateTextureSamplingMode(colorTexture.samplingMode, packedInternal);
                const colorReference = CreateTextureReference(`scatter color (${mat.name})`, packedInternal, colorTexture);
                strengthTexture = CreateTextureReference(`scatter strength (${mat.name})`, packedInternal, fractionTexture);
                ownedTextures.push(colorReference, strengthTexture);
                colorTexture = colorReference;
            } else {
                strengthTexture = packed;
            }
            fractionTexture.dispose(); // intermediate; the packed texture replaces it
        } else if (colorTexture) {
            // Exported without packing: still re-encode linear data, keeping the source filtering.
            const encoded = await this._encodeColorTextureAsSrgbAsync(`scatter color (${mat.name})`, colorTexture, { samplingMode: colorTexture.samplingMode });
            if (encoded) {
                ownedTextures.push(encoded);
                colorTexture = encoded;
            }
        }

        const color = mat.subsurfaceColor;
        return {
            strengthFactor,
            strengthTexture,
            colorFactor: [color.r, color.g, color.b],
            colorTexture,
            anisotropy: mat.subsurfaceScatterAnisotropy,
            ownedTextures,
        };
    }

    /**
     * glTF reads multiscatterColorTexture as sRGB, but the exporter writes texels as stored. Linear textures and
     * hardware sRGB buffers both sample as linear values, so bake their RGB into a new sRGB-encoded texture that
     * keeps the source UV set, wrap modes and transform. Textures that already store sRGB bytes need no change.
     * @param name the name of the encoded texture
     * @param texture the source color texture, which is not modified or disposed
     * @param outputOptions render-target options for the encoded texture
     * @returns a new texture owned by the caller, or null when the source can be exported as-is
     */
    private async _encodeColorTextureAsSrgbAsync(name: string, texture: BaseTexture, outputOptions: ITextureProcessorOutputOptions): Promise<Nullable<BaseTexture>> {
        if (texture.gammaSpace && !texture.getInternalTexture()?._useSRGBBuffer) {
            return null;
        }
        const encoded = await ExtractChannelAsync(name, CreateTextureOperand(texture), TextureChannel.RGBA, texture.getScene()!, TextureColorSpace.SRGB, undefined, outputOptions);
        return encoded.texture;
    }

    /**
     * After exporting a material
     * @param context GLTF context of the material
     * @param node exported GLTF node
     * @param babylonMaterial corresponding babylon material
     * @returns promise that resolves with the updated node
     */
    // eslint-disable-next-line no-restricted-syntax
    public postExportMaterialAsync?(context: string, node: IMaterial, babylonMaterial: Material): Promise<IMaterial> {
        return new Promise((resolve) => {
            if (babylonMaterial instanceof OpenPBRMaterial && this._isExtensionEnabled(babylonMaterial)) {
                this._wasUsed = true;

                const result = this._scatterResults.get(babylonMaterial);

                const scatterInfo: IKHRMaterialsScatter = {
                    scatterStrengthFactor: result?.strengthFactor ?? 1.0,
                    scatterAnisotropy: result?.anisotropy ?? 0,
                };

                // Scatter strength (thin-walled bakes a subsurface-fraction texture; volumetric is always 1).
                if (result?.strengthTexture) {
                    this._exporter._materialNeedsUVsSet.add(babylonMaterial);
                    const strengthTextureInfo = this._exporter._materialExporter.getTextureInfo(result.strengthTexture);
                    if (strengthTextureInfo) {
                        scatterInfo.scatterStrengthFactor = 1.0;
                        scatterInfo.scatterStrengthTexture = strengthTextureInfo;
                    }
                }

                // Multi-scatter color (blended albedo for volumetric; subsurface_color for thin-walled).
                if (result) {
                    scatterInfo.multiscatterColorFactor = result.colorFactor;
                }
                if (result?.colorTexture) {
                    this._exporter._materialNeedsUVsSet.add(babylonMaterial);
                    const colorTextureInfo = this._exporter._materialExporter.getTextureInfo(result.colorTexture);
                    if (colorTextureInfo) {
                        scatterInfo.multiscatterColorTexture = colorTextureInfo;
                    }
                }

                node.extensions = node.extensions || {};
                node.extensions[NAME] = scatterInfo;
            }
            resolve(node);
        });
    }
}

let _Registered = false;
/**
 * Registers the KHR_materials_scatter glTF serializer extension with the {@link GLTFExporter}.
 * Safe to call multiple times; only the first call has an effect.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function RegisterKHR_materials_scatter(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    GLTFExporter.RegisterExtension(NAME, (exporter) => new KHR_materials_scatter(exporter), 101);
}
